<!--
  AddressStep — the in-chat address editor card (LocationIQ autocomplete). Serves both
  `mp-zone` (marketplace) and `mam-zone` (MAM); only the CTA label differs, passed
  in by the orchestrator. Owns its own useAddressAutocomplete instance (config injected
  from the island shell) and emits `confirm(AddressSuggestion)` — carrying the canonical
  geo_* set — once an address is picked and the CTA is clicked. `initialLabel`/`initialCity`/
  `initialGeo` restore the previous pick on back-navigation, so confirming it unchanged emits
  the same complete address.
-->
<template>
  <div class="landing-card">
    <div class="landing-adresse-editor">
      <div class="adresse-input-wrap">
        <span class="adresse-pin" aria-hidden="true">📍</span>
        <input
          v-model="query"
          class="adresse-input-field"
          placeholder="Ex : 15 rue de la Paix, 75001 Paris"
          autocomplete="off"
          aria-label="Votre adresse"
          @input="onInput"
          @keydown.enter.prevent
        />
        <span v-if="confirmed" class="adresse-input-ok" aria-hidden="true">✓</span>
      </div>
      <div v-if="suggestions.length" class="adresse-dropdown" role="listbox" aria-label="Adresses proposées">
        <button
          v-for="s in suggestions"
          :key="s.label"
          type="button"
          role="option"
          class="adresse-sugg-item"
          @click="pick(s)"
        >
          <span class="adresse-sugg-icon" aria-hidden="true">📍</span>{{ s.label }}
        </button>
      </div>
      <!-- Helper mirroring the legacy marketplace.search.enter_nearby_address message:
           LocationIQ won't always return an exact match, so nudge towards a nearby address. -->
      <p class="adresse-hint">
        Votre adresse n’est pas proposée ? Indiquez en une proche de chez vous
      </p>
    </div>
    <button type="button" class="cta cta-blue" :disabled="!confirmed" @click="onConfirm">{{ ctaLabel }}</button>
  </div>
</template>

<script setup lang="ts">
import { inject } from 'vue'
import {
  useAddressAutocomplete,
  ADDRESS_CONFIG_KEY,
  type AddressConfig,
  type AddressGeo,
  type AddressSuggestion,
} from '../../composables/useAddressAutocomplete'

const props = withDefaults(
  defineProps<{
    ctaLabel?: string
    initialLabel?: string | null
    initialCity?: string | null
    initialGeo?: AddressGeo | null
  }>(),
  { ctaLabel: "C'est parti", initialLabel: null, initialCity: null, initialGeo: null },
)

const emit = defineEmits<{ confirm: [address: AddressSuggestion] }>()

// LocationIQ key + brand country codes come from the island shell (provide/inject); a keyless
// fallback keeps the component mountable in isolation (tests/Storybook) with no live suggestions.
const KEYLESS: AddressConfig = { apiKey: '', countryCodes: 'FR', locale: 'fr' }
const { query, suggestions, confirmed, onInput, prefill, pick } = useAddressAutocomplete(
  inject(ADDRESS_CONFIG_KEY, KEYLESS),
)

// Prefill synchronously during setup (not onMounted) so the first render already
// reflects a restored address on back-navigation — no extra tick needed.
if (props.initialLabel) prefill(props.initialLabel, props.initialCity || props.initialLabel, props.initialGeo)

function onConfirm(): void {
  if (confirmed.value) emit('confirm', confirmed.value)
}
</script>

<style scoped>
.landing-card {
  width: 92%;
  margin: 0 auto;
  background: var(--white);
  border: 1px solid #e5e9f0; /* off-palette — DS pass to arbitrate */
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  animation: fadeUp 0.3s both;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.landing-adresse-editor {
  background: #f8fbfd; /* off-palette — DS pass to arbitrate */
  border: 1px solid #e2edf5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.adresse-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1.5px solid #d1d9e0;
  border-radius: 10px;
  background: var(--white);
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.adresse-input-wrap:focus-within {
  border-color: var(--brand-default);
  box-shadow: 0 0 0 3px rgba(45, 163, 237, 0.15);
}
.adresse-pin {
  font-size: 16px;
  flex-shrink: 0;
}
.adresse-input-field {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 600;
  color: var(--navy-800);
  font-family: inherit;
  background: transparent;
}
.adresse-input-field::placeholder {
  color: #9bbdd4;
  font-weight: 500;
}
.adresse-input-ok {
  font-size: 14px;
  font-weight: 700;
  color: var(--brand-default);
  flex-shrink: 0;
}
.adresse-dropdown {
  border: 1px solid #f0f5f9;
  border-radius: 10px;
  background: var(--white);
  max-height: 200px;
  overflow-y: auto;
}
.adresse-sugg-item {
  display: flex;
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  font: inherit;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  font-size: 12.5px;
  color: var(--navy-800);
  cursor: pointer;
  border-bottom: 1px solid #f8fbfd;
  transition: background 0.1s;
}
.adresse-sugg-item:last-child {
  border-bottom: none;
}
.adresse-sugg-item:hover {
  background: var(--blue-100);
}
.adresse-sugg-icon {
  font-size: 13px;
  flex-shrink: 0;
  color: var(--grey-500);
}
.adresse-hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  color: var(--grey-500);
}
.cta {
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 12px;
  background: var(--brand-default);
  color: var(--white);
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  line-height: 14px;
  transition:
    background 0.15s,
    opacity 0.15s;
}
.cta:hover:not(:disabled) {
  background: var(--blue-800);
}
.cta:disabled {
  background: #d1d9e0; /* off-palette disabled grey — DS pass to arbitrate */
  color: var(--grey-500);
  cursor: not-allowed;
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .landing-card {
    animation: none;
  }
}
</style>
